import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowsClockwise,
  Brain,
  CaretDown,
  Crosshair,
  Eye,
  EyeSlash,
  Info,
  MagnifyingGlass,
  Minus,
  Path,
  Plus,
  Quotes,
  Sparkle,
  X,
} from "@phosphor-icons/react";
import archive from "./generatedThoughts.json";

const CANVAS = { width: 2680, height: 1780 };

const islandLayouts = [
  { x: 120, y: 120, w: 600, h: 390, tone: "cyan", name: "Trigger Reef" },
  { x: 835, y: 90, w: 500, h: 350, tone: "blue", name: "Evidence Bay" },
  { x: 1510, y: 105, w: 460, h: 330, tone: "violet", name: "Modeling Atoll" },
  { x: 2100, y: 180, w: 430, h: 390, tone: "aqua", name: "Memory Current" },
  { x: 1830, y: 650, w: 650, h: 420, tone: "pink", name: "Calibration Shoals" },
  { x: 1080, y: 570, w: 570, h: 380, tone: "gold", name: "Atlas Headland" },
  { x: 250, y: 630, w: 650, h: 440, tone: "cyan", name: "Orientation Isles" },
  { x: 150, y: 1230, w: 520, h: 340, tone: "blue", name: "Complexity Lagoon" },
  { x: 790, y: 1180, w: 650, h: 400, tone: "violet", name: "Meaning Delta" },
  { x: 1530, y: 1190, w: 470, h: 340, tone: "pink", name: "Audit Breakwater" },
  { x: 2110, y: 1190, w: 450, h: 360, tone: "gold", name: "Embodiment Cove" },
];

const relationMeta = {
  activated: { label: "Activated", className: "activated" },
  coactive: { label: "Co-active", className: "coactive" },
  persisted: { label: "Persisted", className: "persisted" },
  returned: { label: "Returned", className: "returned" },
  corrected: { label: "Corrected", className: "corrected" },
  generalized: { label: "Generalized", className: "generalized" },
  interrupted: { label: "Interrupted", className: "interrupted" },
};

const crossLinks = [
  { from: 1, to: 12, type: "generalized", label: "lived uncertainty becomes evidence" },
  { from: 1, to: 7, type: "persisted", label: "deadline grief remains active" },
  { from: 7, to: 10, type: "persisted", label: "deadline thread" },
  { from: 10, to: 11, type: "persisted", label: "accuracy demanded" },
  { from: 11, to: 13, type: "generalized", label: "preserve source, then explain" },
  { from: 4, to: 17, type: "returned", label: "observer becomes object" },
  { from: 6, to: 15, type: "returned", label: "analytical-life question returns" },
  { from: 8, to: 19, type: "generalized", label: "cascade becomes novelty hypothesis" },
  { from: 9, to: 57, type: "generalized", label: "sequence requires provenance" },
  { from: 17, to: 23, type: "activated", label: "database instinct named" },
  { from: 18, to: 24, type: "returned", label: "neuroscience branch resumes" },
  { from: 20, to: 32, type: "corrected", label: "audit is pressure-tested" },
  { from: 23, to: 43, type: "generalized", label: "evidence asks for orientation" },
  { from: 31, to: 60, type: "generalized", label: "retrieval becomes navigation" },
  { from: 47, to: 71, type: "returned", label: "ideas earn and lose their place" },
  { from: 52, to: 73, type: "activated", label: "Atlas recruits semantic mapping" },
  { from: 65, to: 79, type: "generalized", label: "disorientation requires external structure" },
  { from: 70, to: 81, type: "activated", label: "folders become reasoning history" },
  { from: 76, to: 79, type: "corrected", label: "graph must earn its existence" },
  { from: 81, to: 88, type: "generalized", label: "failure validates auditability" },
  { from: 83, to: 88, type: "generalized", label: "interpretation revision exposed" },
  { from: 92, to: 65, type: "corrected", label: "mapping is not living" },
];

const persistentThreads = [
  { id: "deadline", label: "Deadline grief", color: "#ff70b7", moments: [1, 7, 10, 11, 15, 49] },
  { id: "nathan", label: "Nathan uncertainty", color: "#a789ff", moments: [1, 2, 3, 12, 14, 21] },
  { id: "provenance", label: "Accuracy + provenance", color: "#58e6ff", moments: [9, 11, 13, 57, 76, 83, 86, 87, 88] },
  { id: "cognition", label: "What does my brain do?", color: "#62f2c5", moments: [6, 15, 17, 18, 24, 28, 31, 41, 67, 80, 81, 83] },
  { id: "novelty", label: "Absurd-event hypothesis", color: "#ffc96b", moments: [8, 19, 20, 32, 84] },
  { id: "atlas", label: "Orientation / Atlas", color: "#6da8ff", moments: [43, 48, 50, 51, 52, 54, 55, 57, 59, 60, 64, 65, 66, 73, 79, 82] },
];

function cleanVoice(value) {
  return value.replace(/^['“\"]|['”\"]$/g, "");
}

function candidateFunction(node) {
  const text = `${node.title} ${node.thoughts.join(" ")}`.toLowerCase();
  if (/memory|retrieve|word|lexical|alphabet/.test(text)) return "Associative retrieval + lexical access";
  if (/nathan|relationship|affection|motive|carissa/.test(text)) return "Social inference + competing-model construction";
  if (/audit|evidence|predict|prove|valid|source|codebook/.test(text)) return "Evidence evaluation + executive monitoring";
  if (/atlas|map|navigation|where i am|guidebook/.test(text)) return "Spatial-conceptual navigation";
  if (/brain|cognition|complexity|metacognition|thinking/.test(text)) return "Metacognitive monitoring + model integration";
  if (/work|grocery|deadline|sleep|calendar/.test(text)) return "Salience detection + task-context switching";
  if (/pattern|taurus|body|embod/.test(text)) return "Interoceptive meaning-making";
  return "Active model construction";
}

function edgeTypeFromMovement(movement = "") {
  const text = movement.toLowerCase();
  if (text.includes("return")) return "returned";
  if (text.includes("interrupt") || text.includes("side quest")) return "interrupted";
  if (text.includes("general")) return "generalized";
  if (text.includes("correct")) return "corrected";
  return "activated";
}

function layoutMoments() {
  const nodes = [];
  archive.phases.forEach((phase, phaseIndex) => {
    const island = islandLayouts[phaseIndex];
    const count = phase.moments.length;
    phase.moments.forEach((moment, index) => {
      const columns = Math.min(4, Math.max(2, Math.ceil(Math.sqrt(count * 1.35))));
      const rows = Math.ceil(count / columns);
      const col = index % columns;
      const row = Math.floor(index / columns);
      const x = island.x + 100 + (col * (island.w - 200)) / Math.max(1, columns - 1) + (row % 2 ? 22 : -10);
      const y = island.y + 115 + (row * (island.h - 205)) / Math.max(1, rows - 1);
      nodes.push({ ...moment, phaseId: phase.id, phaseTitle: phase.title, island, x, y });
    });
  });
  return nodes;
}

function curvePath(a, b, bend = 0.18) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const mx = (a.x + b.x) / 2 - dy * bend;
  const my = (a.y + b.y) / 2 + dx * bend;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
}

export default function App() {
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const nodes = useMemo(layoutMoments, []);
  const nodeMap = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const sequentialEdges = useMemo(() => nodes.slice(0, -1).map((node, index) => ({
    from: node.id,
    to: nodes[index + 1].id,
    type: edgeTypeFromMovement(nodes[index + 1].movement),
    label: nodes[index + 1].movement,
  })), [nodes]);
  const allEdges = useMemo(() => [...sequentialEdges, ...crossLinks], [sequentialEdges]);

  const [selectedId, setSelectedId] = useState(1);
  const [scale, setScale] = useState(0.38);
  const [offset, setOffset] = useState({ x: 28, y: 30 });
  const [query, setQuery] = useState("");
  const [depth, setDepth] = useState("medium");
  const [layers, setLayers] = useState({ threads: true, provenance: true, science: false, narrative: true });
  const [activeRelation, setActiveRelation] = useState("all");
  const [activeThread, setActiveThread] = useState("all");
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const selected = nodeMap.get(selectedId) ?? nodes[0];

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return new Set();
    return new Set(nodes.filter((node) => `${node.id} ${node.title} ${node.thoughts.join(" ")} ${node.phaseTitle}`.toLowerCase().includes(term)).map((node) => node.id));
  }, [nodes, query]);
  const effectiveDepth = scale < 0.5 ? "collapsed" : scale < 0.76 && depth === "expanded" ? "medium" : depth;

  const visibleThreads = activeThread === "all" ? persistentThreads : persistentThreads.filter((thread) => thread.id === activeThread);

  function toggleLayer(key) {
    setLayers((current) => {
      const nextValue = !current[key];
      if (nextValue && (key === "narrative" || key === "provenance" || key === "science")) {
        setInspectorOpen(true);
      }
      return { ...current, [key]: nextValue };
    });
  }

  function zoom(delta) {
    setScale((value) => Math.min(1.55, Math.max(0.28, Number((value + delta).toFixed(2)))));
  }

  function fitMap() {
    const el = viewportRef.current;
    if (!el) return;
    const next = Math.min((el.clientWidth - 42) / CANVAS.width, (el.clientHeight - 42) / CANVAS.height);
    setScale(Math.max(0.28, Math.min(0.72, next)));
    setOffset({ x: 20, y: 20 });
  }

  function focusNode(node) {
    const el = viewportRef.current;
    if (!el) return;
    const nextScale = Math.max(scale, 0.82);
    setScale(nextScale);
    setOffset({
      x: el.clientWidth / 2 - node.x * nextScale - (inspectorOpen ? 190 : 0),
      y: el.clientHeight / 2 - node.y * nextScale,
    });
  }

  function chooseNode(node) {
    setSelectedId(node.id);
    setInspectorOpen(true);
  }

  function onPointerDown(event) {
    if (event.button !== 0 || event.target.closest("button, input, select, .thought-card")) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, offset };
  }

  function onPointerMove(event) {
    if (!dragRef.current) return;
    setOffset({
      x: dragRef.current.offset.x + event.clientX - dragRef.current.x,
      y: dragRef.current.offset.y + event.clientY - dragRef.current.y,
    });
  }

  function onPointerUp(event) {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const wheel = (event) => {
      if (Math.abs(event.deltaY) < 1) return;
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const next = Math.min(1.55, Math.max(0.28, scale * (event.deltaY > 0 ? 0.92 : 1.08)));
      const world = { x: (pointer.x - offset.x) / scale, y: (pointer.y - offset.y) / scale };
      setScale(next);
      setOffset({ x: pointer.x - world.x * next, y: pointer.y - world.y * next });
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, [scale, offset]);

  useEffect(() => {
    const frame = requestAnimationFrame(fitMap);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <div className="brand-mark"><Sparkle weight="fill" /></div>
          <div>
            <p>EKHO LAB · LIVING ARCHIVE</p>
            <h1>Cognition Archipelago</h1>
          </div>
        </div>
        <div className="header-summary">
          <span><strong>{archive.momentCount}</strong> compound thought nodes</span>
          <span><strong>{archive.phaseCount}</strong> cognitive islands</span>
          <span><strong>{crossLinks.length}</strong> long-range bridges</span>
        </div>
        <button className="about-button" onClick={() => setInspectorOpen(true)}><Info /> How to read it</button>
      </header>

      <section className="toolbar" aria-label="Map controls">
        <label className="search-box">
          <MagnifyingGlass />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a thought, quote, phase…" />
          {query && <button onClick={() => setQuery("")} aria-label="Clear search"><X /></button>}
        </label>
        <div className="depth-control" role="group" aria-label="Node detail depth">
          <span>Node depth</span>
          {["collapsed", "medium", "expanded"].map((option) => (
            <button key={option} className={depth === option ? "active" : ""} onClick={() => setDepth(option)}>{option}</button>
          ))}
        </div>
        <div className="layer-controls">
          <LayerButton active={layers.narrative} onClick={() => toggleLayer("narrative")} icon={Quotes}>Voice</LayerButton>
          <LayerButton active={layers.threads} onClick={() => toggleLayer("threads")} icon={Path}>Threads</LayerButton>
          <LayerButton active={layers.provenance} onClick={() => toggleLayer("provenance")} icon={Crosshair}>Provenance</LayerButton>
          <LayerButton active={layers.science} onClick={() => toggleLayer("science")} icon={Brain}>Science lens</LayerButton>
        </div>
      </section>

      <section className="map-stage">
        <aside className="legend-panel">
          <p className="panel-kicker">Edge semantics</p>
          <button className={activeRelation === "all" ? "active" : ""} onClick={() => setActiveRelation("all")}><span className="legend-all" />All relationships</button>
          {Object.entries(relationMeta).map(([key, meta]) => (
            <button key={key} className={activeRelation === key ? "active" : ""} onClick={() => setActiveRelation(key)}>
              <span className={`legend-line ${meta.className}`} />{meta.label}
            </button>
          ))}
          <div className="legend-divider" />
          <p className="panel-kicker">Persistent threads</p>
          <select value={activeThread} onChange={(event) => setActiveThread(event.target.value)} disabled={!layers.threads}>
            <option value="all">All persistent threads</option>
            {persistentThreads.map((thread) => <option key={thread.id} value={thread.id}>{thread.label}</option>)}
          </select>
          <p className="legend-note">Lines connect thoughts to thoughts. Quotes and interpretations live inside each node as provenance.</p>
        </aside>

        <div
          className={`map-viewport depth-${effectiveDepth}`}
          ref={viewportRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="layer-status" aria-live="polite">
            <span>VISIBLE LAYERS</span>
            {layers.narrative && <strong className="status-voice"><Quotes /> Voice</strong>}
            {layers.threads && <strong className="status-threads"><Path /> Threads</strong>}
            {layers.provenance && <strong className="status-provenance"><Crosshair /> Provenance</strong>}
            {layers.science && <strong className="status-science"><Brain /> Science</strong>}
            {!Object.values(layers).some(Boolean) && <em>Base map only</em>}
          </div>
          <div className="star-field" />
          <div className="map-canvas" style={{ width: CANVAS.width, height: CANVAS.height, transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}>
            <div className="cartography-title"><span>THE LIVING MAP</span><strong>Follow the thought, not the transcript.</strong></div>
            {archive.phases.map((phase, index) => <Island key={phase.id} phase={phase} layout={islandLayouts[index]} />)}

            <svg className="edge-layer" width={CANVAS.width} height={CANVAS.height} aria-hidden="true">
              <defs>
                <filter id="glow"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
              </defs>
              {layers.threads && visibleThreads.map((thread) => {
                const points = thread.moments.map((id) => nodeMap.get(id)).filter(Boolean);
                return points.slice(0, -1).map((node, index) => (
                  <path key={`${thread.id}-${node.id}`} className="persistent-thread" style={{ stroke: thread.color }} d={curvePath(node, points[index + 1], 0.08)} />
                ));
              })}
              {allEdges.map((edge, index) => {
                const from = nodeMap.get(edge.from);
                const to = nodeMap.get(edge.to);
                if (!from || !to) return null;
                const muted = activeRelation !== "all" && activeRelation !== edge.type;
                return <path key={`${edge.from}-${edge.to}-${index}`} className={`map-edge ${edge.type} ${muted ? "muted" : ""}`} d={curvePath(from, to, edge.from + 1 === edge.to ? 0.08 : 0.16)} />;
              })}
            </svg>

            {nodes.map((node) => {
              const isMatch = matches.has(node.id);
              const dim = query && !isMatch;
              const related = allEdges.some((edge) => (edge.from === selectedId && edge.to === node.id) || (edge.to === selectedId && edge.from === node.id));
              return <ThoughtNode key={node.id} node={node} selected={node.id === selectedId} related={related} dim={dim} coactiveFocus={activeRelation === "coactive" && node.thoughts.length > 1} depth={effectiveDepth} showNarrative={layers.narrative} showProvenance={layers.provenance} showScience={layers.science} onSelect={() => chooseNode(node)} />;
            })}

            <div className="you-are-here" style={{ left: selected.x, top: selected.y }}><Crosshair weight="bold" /><span>YOU ARE HERE</span></div>
          </div>
          <div className="zoom-controls">
            <button onClick={() => zoom(0.1)} aria-label="Zoom in"><Plus /></button>
            <button onClick={() => zoom(-0.1)} aria-label="Zoom out"><Minus /></button>
            <button onClick={fitMap} aria-label="Fit whole map"><ArrowsClockwise /></button>
            <span>{Math.round(scale * 100)}%</span>
          </div>
          <div className="map-hint">Drag to travel · Scroll to zoom · Click a thought to open its depth</div>
        </div>

        {inspectorOpen && <Inspector node={selected} nodeMap={nodeMap} edges={allEdges} layers={layers} onClose={() => setInspectorOpen(false)} onChoose={(id) => { const node = nodeMap.get(id); if (node) { chooseNode(node); focusNode(node); } }} onFocus={() => focusNode(selected)} />}
      </section>
    </main>
  );
}

function LayerButton({ active, onClick, icon: Icon, children }) {
  return <button className={active ? "active" : ""} onClick={onClick} aria-pressed={active}>{active ? <Eye /> : <EyeSlash />}<Icon />{children}</button>;
}

function Island({ phase, layout }) {
  return (
    <section className={`island island-${layout.tone}`} style={{ left: layout.x, top: layout.y, width: layout.w, height: layout.h }} aria-label={`Phase ${phase.id}: ${phase.title}`}>
      <div className="island-rings" />
      <div className="island-label"><span>ISLAND {String(phase.id).padStart(2, "0")}</span><strong>{layout.name}</strong><small>{phase.title}</small></div>
    </section>
  );
}

function ThoughtNode({ node, selected, related, dim, coactiveFocus, depth, showNarrative, showProvenance, showScience, onSelect }) {
  const voice = cleanVoice(node.voice);
  return (
    <button
      className={`thought-card ${selected ? "selected" : ""} ${related ? "related" : ""} ${dim ? "dim" : ""} ${coactiveFocus ? "coactive-focus" : ""} ${showNarrative ? "voice-on" : ""} ${showProvenance ? "provenance-on" : ""} ${showScience ? "science-on" : ""}`}
      style={{ left: node.x, top: node.y }}
      onClick={onSelect}
      aria-label={`Moment ${node.id}: ${node.title}`}
    >
      <span className="node-orbit" />
      <span className="moment-number">M{node.id}</span>
      {depth !== "collapsed" && <span className="node-title">{node.title}</span>}
      {depth !== "collapsed" && showNarrative && <span className="node-voice">“{voice}”</span>}
      {depth === "expanded" && <span className="node-meaning">Normalized: {node.title}</span>}
      {showProvenance && <span className={`provenance-pip ${node.voiceExact ? "exact" : "reconstructed"}`} title={node.voiceExact ? "Exact Katie wording" : "Supplied reconstruction"}><Crosshair weight="bold" /></span>}
      {showScience && <span className="science-pip"><Brain weight="fill" /></span>}
      <span className="coactive-count">{node.thoughts.length}</span>
    </button>
  );
}

function Inspector({ node, nodeMap, edges, layers, onClose, onChoose, onFocus }) {
  const [expanded, setExpanded] = useState(true);
  const neighbors = edges.filter((edge) => edge.from === node.id || edge.to === node.id).map((edge) => ({
    ...edge,
    direction: edge.from === node.id ? "activated" : "arrived from",
    node: nodeMap.get(edge.from === node.id ? edge.to : edge.from),
  })).filter((item) => item.node);

  return (
    <aside className="inspector" aria-label="Selected thought details">
      <div className="inspector-head">
        <div><p>COMPOUND THOUGHT NODE · M{node.id}</p><h2>{node.title}</h2></div>
        <button onClick={onClose} aria-label="Close inspector"><X /></button>
      </div>
      <button className="focus-button" onClick={onFocus}><Crosshair /> Center this thought on the map</button>

      {layers.narrative && <section className="voice-card">
        <p><Quotes weight="fill" /> {node.voiceExact ? "EXACT KATIE WORDING" : "KATIE VOICE / SUPPLIED RECONSTRUCTION"}</p>
        <blockquote>“{cleanVoice(node.voice)}”</blockquote>
      </section>}

      <section className="meaning-card">
        <p>NORMALIZED PROPOSITION</p>
        <h3>{node.title}</h3>
        {node.next && <span>{node.next}</span>}
      </section>

      <button className="section-toggle" onClick={() => setExpanded((value) => !value)}>
        <span><Sparkle /> Co-active thoughts <strong>{node.thoughts.length}</strong></span><CaretDown className={expanded ? "rotated" : ""} />
      </button>
      {expanded && <div className="coactive-grid">
        {node.thoughts.map((thought, index) => <div key={`${node.id}-${index}`}><span>{index + 1}</span><p>{thought}</p></div>)}
      </div>}

      <section className="movement-card">
        <p>WHAT THIS THOUGHT DID NEXT</p>
        <strong>{node.movement || "Continuation"}</strong>
        <span>{node.next}</span>
      </section>

      {layers.provenance && <section className="provenance-card">
        <p><Crosshair /> PROVENANCE</p>
        <dl>
          <div><dt>Source</dt><dd>{node.source}</dd></div>
          <div><dt>Status</dt><dd>{node.voiceExact ? "Exact wording preserved in supplied sequence" : "Conversation-derived reconstruction; not presented as a verbatim quote"}</dd></div>
          <div><dt>Confidence</dt><dd>High for supplied sequence; not a neural measurement</dd></div>
          <div><dt>Rule</dt><dd>Quote + formal meaning are metadata inside this one node.</dd></div>
        </dl>
      </section>}

      {layers.science && <section className="science-card">
        <p><Brain /> OPTIONAL SCIENCE LENS</p>
        <h3>{candidateFunction(node)}</h3>
        <span>Candidate functional interpretation based on the expressed cognitive move. No brain region was measured.</span>
      </section>}

      <section className="connections">
        <p>ACTIVATION RELATIONSHIPS</p>
        {neighbors.length ? neighbors.slice(0, 10).map((item, index) => (
          <button key={`${item.from}-${item.to}-${index}`} onClick={() => onChoose(item.node.id)}>
            <span className={`connection-mark ${item.type}`} />
            <span><small>{relationMeta[item.type]?.label} · {item.direction}</small><strong>M{item.node.id} · {item.node.title}</strong></span>
          </button>
        )) : <span className="empty-connection">No mapped neighbor yet.</span>}
      </section>

      <div className="inspector-boundary"><Info /> This is an activation topology with an auditable narrative/provenance overlay. It is not fMRI data and does not claim measured neural activation.</div>
    </aside>
  );
}
